import { useMemo } from 'react';
import { useDispatch, useSelector as useSelectorOrigin } from 'react-redux';

import * as card from '../../nodes/card';
import * as flipbook from '../../nodes/flipbook';
import * as lastPage from '../../nodes/lastPage';
import * as drawer from '../../nodes/drawer';
import * as toast from '../../nodes/toast';
import * as share from '../../nodes/share';
import * as gift from '../../nodes/gift';
import * as hint from '../../nodes/hint';
import * as promo from '../../nodes/promo';
import * as invitation from '../../nodes/invitation';

import { IState } from '../../types';

export const useSelector = <TSelected>(
  selector: (state: IState) => TSelected,
  equalityFn?: (left: TSelected, right: TSelected) => boolean,
) => useSelectorOrigin<IState, TSelected>(selector, equalityFn);

const withHandler = <U extends {[key: string]: object}>(actionsMap:U, handler:ProxyHandler<any>):U => {
  const result = {} as {[key: string]: object};
  Object.keys(actionsMap).forEach((name) => {
    result[name] = new Proxy(actionsMap[name], handler);
  });
  return result as typeof actionsMap;
};

export const useDispatcher = () => {
  const dispatch = useDispatch();

  return useMemo(() => {
    const handler = {
      get(target: any, propertyName: PropertyKey): any {
        return (...args: any[]) => {
          const action = target[propertyName].apply(null, args);
          dispatch(action);
        };
      },
    };

    return withHandler({
      card: card.actions,
      flipbook: flipbook.actions,
      lastPage: lastPage.actions,
      drawer: drawer.actions,
      toast: toast.actions,
      share: share.actions,
      gift: gift.actions,
      hint: hint.actions,
      promo: promo.actions,
      invitation: invitation.actions,
    }, handler);
  }, [dispatch]);
};

export {
  useDispatch,
};
